import { useFormikContext } from 'formik'
import React, { ChangeEvent, Fragment, useEffect } from 'react'
import { CreatePendencyFormValues } from '../..'
import {
  PendencyAfterOperatorRequestListEnum,
  PendencyAfterOperatorRequestListEnumLocalized
} from 'common/enum/actions-pendency'
import { capitalizeEachWordText } from 'common/utils/capitalizeEachWordText'
import { greetingMessage } from 'presentation/utils/greetingMessage'
import CardInputMessage from '../../CardInputMessage'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import CheckboxNew from 'presentation/shared/components/CheckboxNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import CardMessagePreview from '../../CardMessagePreview'
import RMarkdown from 'presentation/hospital/components/RMarkdown'
import { ReactComponent as DoubleArrowIcon } from 'presentation/assets/icons/double-arrow.svg'
import * as S from '../styles'

type PendencyTabProps = {
  title: string
  handleCheckboxChange?: (event: ChangeEvent<HTMLInputElement>) => void
  isOptionChecked?: (doc: string) => boolean
}

const PendencyAfterOperatorRequest = ({
  title,
  isOptionChecked,
  handleCheckboxChange
}: PendencyTabProps) => {
  const { values, errors, isValid, handleChange, setFieldValue } =
    useFormikContext<CreatePendencyFormValues>()

  let selectedOptionsList = ''

  values?.selected_options?.forEach((doc) => {
    const optionChecked = isOptionChecked?.(
      PendencyAfterOperatorRequestListEnum[
        doc as keyof typeof PendencyAfterOperatorRequestListEnum
      ]
    )
    const optionCheckedValue =
      values[
        `OPTION_${
          PendencyAfterOperatorRequestListEnum[
            doc as keyof typeof PendencyAfterOperatorRequestListEnum
          ]
        }_TEXT` as keyof CreatePendencyFormValues
      ]

    const optionName =
      PendencyAfterOperatorRequestListEnumLocalized[
        doc as keyof typeof PendencyAfterOperatorRequestListEnum
      ]

    selectedOptionsList +=
      optionChecked && optionCheckedValue
        ? `- **${optionName}**  \n${optionCheckedValue}  \n`
        : `- **${optionName}**  \n`
  })

  const messageBody = [
    `Prezado(a) **Dr(a) ${capitalizeEachWordText(
      values?.doctor_name ?? '-'
    )}, ${greetingMessage?.()}** Informamos que após análise do convênio, foram geradas a seguintes pendências:\n\n${selectedOptionsList.trim()}`
  ]

  const rawMessageText = messageBody.join('\n\n')

  useEffect(() => {
    setFieldValue('general_observations', rawMessageText)
  }, [
    values.selected_options,
    values.OPTION_SUPPLEMENTARY_MEDICAL_REPORT_TEXT,
    values.OPTION_EXAM_REPORTS_PHOTOS_TEXT,
    values.OPTION_SUPPLIERS_TEXT
  ])

  return (
    <S.TabBoxWrapper isValid={isValid}>
      <CardInputMessage title={title}>
        <SupportTextNew color="neutral900">
          Prezado(a) Dr(a){' '}
          <SupportTextNew as="span" color="neutral900" weight="bold">
            {values?.doctor_name
              ? capitalizeEachWordText(values?.doctor_name)
              : '-'}
          </SupportTextNew>
          ,{' '}
          <SupportTextNew as="span" color="neutral900" weight="bold">
            {greetingMessage?.()}
          </SupportTextNew>{' '}
          Informamos que após análise do convênio, foram geradas a seguintes
          pendências:
        </SupportTextNew>
        <S.CheckboxWrapper>
          {Array.from(Object.values(PendencyAfterOperatorRequestListEnum)).map(
            (doc) => (
              <Fragment key={doc}>
                <CheckboxNew
                  key={doc}
                  label={PendencyAfterOperatorRequestListEnumLocalized[doc]}
                  labelFor={doc}
                  name={doc}
                  onChange={(e) => handleCheckboxChange?.(e)}
                  checked={isOptionChecked?.(doc)}
                  value={doc}
                />
                {isOptionChecked?.(
                  PendencyAfterOperatorRequestListEnum[doc]
                ) && (
                  <TextAreaNew
                    label="Informação adicional"
                    labelOverlapping
                    labelSize="xxsmall"
                    fontSize="xsmall"
                    placeholder="Descreva mais detalhes sobre esta pendência"
                    required
                    requiredColor="neutral900"
                    name={`OPTION_${PendencyAfterOperatorRequestListEnum[doc]}_TEXT`}
                    value={
                      values[
                        `OPTION_${PendencyAfterOperatorRequestListEnum[doc]}_TEXT` as keyof CreatePendencyFormValues
                      ]
                    }
                    onChange={handleChange}
                    error={
                      errors[
                        `OPTION_${PendencyAfterOperatorRequestListEnum[doc]}_TEXT` as keyof CreatePendencyFormValues
                      ]
                        ? (errors?.[
                            `OPTION_${PendencyAfterOperatorRequestListEnum[doc]}_TEXT`
                          ] as string)
                        : undefined
                    }
                  />
                )}
              </Fragment>
            )
          )}
        </S.CheckboxWrapper>
      </CardInputMessage>
      <DoubleArrowIcon className="double-arrow-icon" width={48} height={48} />
      <CardMessagePreview>
        {values.selected_options && values.selected_options.length > 0 ? (
          <RMarkdown rawText={rawMessageText} />
        ) : (
          <SupportTextNew color="neutral600" size="xsmall">
            Selecione uma ou mais opções ao lado e caso necessário adicione
            texto complementar para visualizar a prévia da mensagem que será
            enviada
          </SupportTextNew>
        )}
      </CardMessagePreview>
    </S.TabBoxWrapper>
  )
}

export default PendencyAfterOperatorRequest
